import { getOnStorage } from '@/shared';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type NetworkStatus = 'offline' | 'online';
interface ConnectivityContextProps {
  connectivityStatus: NetworkStatus;
  updateConnectivityStatus: (status: NetworkStatus) => void;
}

const getOnLineStatus = () =>
  typeof window.navigator !== 'undefined' && typeof window.navigator.onLine === 'boolean'
      ? window.navigator.onLine
      : true;

const NetworkStatusContext = createContext<ConnectivityContextProps>({} as ConnectivityContextProps);

const NetworkStatusProvider: React.FC<any> = ({ children }) => {
  const [connectivityStatus, setConnectivityStatus]= useState<NetworkStatus>(() => {
    const status = getOnLineStatus();
    if (status) return 'online';
    return 'offline';
  });

  useEffect(() => {
    const handleOnline = () => {
      setConnectivityStatus('online');
    };

    const handleOffline = () => {
      setConnectivityStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchConnectivityStatus = useCallback(() => {
    const status = getOnLineStatus();
    if (status) {
      setConnectivityStatus('online');
      return;
    } 
    setConnectivityStatus('offline');
  }, []);

  const updateConnectivityStatus = useCallback((status: NetworkStatus) => {
    setConnectivityStatus(status);
  }, []);

  const connectivityRef = useRef(getOnStorage("network_status") as 'online' | 'offline');
  const connectivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (connectivityTimeoutRef.current) {
      clearTimeout(connectivityTimeoutRef.current);
      connectivityTimeoutRef.current = null;
    }
    const status = getOnStorage("network_status");
    if (status && connectivityRef.current !== connectivityStatus && connectivityRef.current !== undefined && connectivityRef.current !== status) {
      if (status) {
        setConnectivityStatus(status);
        connectivityRef.current = status;
      } else {
        connectivityTimeoutRef.current = setTimeout(() => {
          fetchConnectivityStatus();
          connectivityTimeoutRef.current = null;
        }, 500);
      }
      // if (status) 
    }
    return () => {
      if (connectivityTimeoutRef.current) {
        clearTimeout(connectivityTimeoutRef.current);
        connectivityTimeoutRef.current = null;
      }
    };
  }, [connectivityStatus, fetchConnectivityStatus]);

  const contextValue = useMemo(() => {
    return {
      connectivityStatus,
      updateConnectivityStatus,
    }
  }, [connectivityStatus, updateConnectivityStatus]);

  return (
    <NetworkStatusContext.Provider value={contextValue}>
      {children}
    </NetworkStatusContext.Provider>
  )
}

const useNetworkStatusContext = () => {
  const ctx = useContext(NetworkStatusContext);

  if (!ctx) throw new Error("Erro ao usar hook Connectivity Context");

  return ctx;
}

export { NetworkStatusProvider, useNetworkStatusContext };